import React, { useState, useCallback } from 'react';
import {
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  RadioGroup,
  Radio,
  Checkbox,
  NumberInput,
  NumberInputField,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Box,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';

import Card from 'components/card/Card';
import DateField from 'components/fields/DateField';
import { useAppSelector, useAppDispatch } from 'hooks';
import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';
import {
  setTypeOfPolicy,
  updatePolicySection,
  updateProductAgnosticField,
  setPremiumConfirmed,
} from 'reducers/lifeInsuranceFormReducer';
import { apiGetLifeProducts } from 'api';

// Reusable Premium Warning Alert Component
const PremiumWarningAlert: React.FC<{
  premiumAmount: number;
  premiumConfirmed: boolean;
  onConfirmChange: (checked: boolean) => void;
  isLocked: boolean;
}> = ({ premiumAmount, premiumConfirmed, onConfirmChange, isLocked }) => {
  if (premiumAmount <= 10000) return null;

  return (
    <Alert status="warning" mt={2} flexDirection="column" alignItems="flex-start">
      <HStack mb={2}>
        <AlertIcon />
        <AlertDescription>
          Premium amount exceeds $10,000. Please verify this amount is correct.
        </AlertDescription>
      </HStack>
      <Checkbox
        ml={8}
        isChecked={premiumConfirmed}
        onChange={(e) => onConfirmChange(e.target.checked)}
        colorScheme="orange"
        isRequired
        isDisabled={isLocked}
      >
        <Text fontWeight="bold">
          I confirm the premium amount is correct
        </Text>
      </Checkbox>
    </Alert>
  );
};

interface LifePolicySectionProps {
  policyIndex: number;
  isLocked?: boolean;
  canEditPremium?: boolean;
  onRemove?: () => void;
  showRemoveButton?: boolean;
}

const POLICY_TYPES = [
  'Life insurance',
  'Critical Illness',
  'Disability',
  'Segregated Funds',
  'Group Benefits',
  'Group Retirement Services',
  'Individual health and dental',
  'Travel',
  'Other',
];


const LifePolicySection = ({
  policyIndex,
  isLocked = false,
  canEditPremium = false,
  onRemove,
  showRemoveButton = false,
}: LifePolicySectionProps) => {
  const dispatch = useAppDispatch();

  // Fetch life products to get carriers
  const { data: lifeProductsResponse } = useQuery({
    queryKey: ['life-products'],
    queryFn: apiGetLifeProducts,
    staleTime: 30 * 60 * 1000,
  });

  // Extract results array from paginated response
  const lifeProducts = lifeProductsResponse?.results || [];

  // Helper function to get carriers for a policy type from API
  const getCarriersForPolicyType = (policyType: string, fallback: string[]): string[] => {
    // Ensure lifeProducts is an array
    if (!Array.isArray(lifeProducts) || lifeProducts.length === 0) {
      return fallback;
    }

    const productNameMap: Record<string, string> = {
      'Life insurance': 'Life Insurance',
      'Critical Illness': 'Critical Illness',
      'Disability': 'Disability',
      'Segregated Funds': 'Segregated Funds',
      'Group Benefits': 'Group Benefits',
      'Group Retirement Services': 'Group Retirement Services',
      'Individual health and dental': 'Individual health and dental',
      'Travel': 'Travel',
    };

    const productName = productNameMap[policyType];
    if (!productName) return fallback;

    // Try exact match first, then case-insensitive match
    let product = lifeProducts.find(
      (p: any) => p.name === productName,
    );
    
    if (!product) {
      product = lifeProducts.find(
        (p: any) => p.name.toLowerCase() === productName.toLowerCase(),
      );
    }
    
    if (product?.carriers && Array.isArray(product.carriers) && product.carriers.length > 0) {
      return product.carriers.map((carrier: any) => carrier.name);
    }
    
    return fallback;
  };

  const typeOfPolicy = useAppSelector(
    (state) => state.lifeInsuranceForm.data.type_of_policy,
  );
  const selectedTypes = typeOfPolicy
    ? typeOfPolicy
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : [];
  const currentType = selectedTypes[policyIndex] || '';

  const formData = useAppSelector((state) => state.lifeInsuranceForm.data);
  const premiumConfirmed = useAppSelector(
    (state) => state.lifeInsuranceForm.premiumConfirmed,
  );

  // Helper to get value from nested policy section
  const getSectionValue = (
    section: string,
    index: number,
    key: string,
    defaultValue: any = '',
  ) => {
    const sectionData = (formData as any)[section];
    if (!Array.isArray(sectionData)) return defaultValue;
    return sectionData[index]?.[key] ?? defaultValue;
  };

  // Helper to get product-agnostic field value
  const getAgnosticValue = (key: string, defaultValue: any = '') => {
    return (formData as any)[key] ?? defaultValue;
  };

  // Update nested policy section field
  const updateSectionField = (
    section: string,
    index: number,
    key: string,
    value: any,
  ) => {
    dispatch(updatePolicySection({ section, index, key, value }));
  };

  // Update product-agnostic field
  const updateAgnosticField = (key: string, value: any) => {
    dispatch(updateProductAgnosticField({ key, value }));
  };

  // Helper to get the section name from policy type
  const getSectionName = (policyType: string): string => {
    const mapping: Record<string, string> = {
      'Life insurance': 'life_insurance',
      'Critical Illness': 'critical_illness',
      'Disability': 'disability',
      'Segregated Funds': 'segregated_funds',
      'Group Benefits': 'group_benefits',
      'Group Retirement Services': 'group_retirement_services',
      'Individual health and dental': 'individual_health_dental',
      'Travel': 'travel',
      'Other': 'other',
    };
    return mapping[policyType] || '';
  };

  // Helper to determine if premium/benefit should be stored in section or as agnostic
  const shouldStoreInSection = (fieldName: string): boolean => {
    // Product types that have premium_amount in their arrays
    const typesWithPremiumInArray = [
      'Life insurance',      // Added to API types
      'Critical Illness',
      'Disability', 
      'Group Benefits',
      'Travel',
      'Individual health and dental'
    ];
    
    // Product types that have benefit_amount in their arrays  
    const typesWithBenefitInArray = [
      'Critical Illness',
      'Disability'
      // Note: Life insurance does NOT have benefit_amount in its array
    ];
    
    if (fieldName === 'premium_amount') {
      return typesWithPremiumInArray.includes(currentType);
    }
    
    if (fieldName === 'benefit_amount') {
      return typesWithBenefitInArray.includes(currentType);
    }
    
    return false;
  };

  // Smart field updater that stores data in the right place
  const updateFieldSmart = (key: string, value: any) => {
    if (shouldStoreInSection(key)) {
      const sectionName = getSectionName(currentType);
      if (sectionName) {
        updateSectionField(sectionName, typeIndex, key, value);
      }
    } else {
      updateAgnosticField(key, value);
    }
  };

  // Smart field getter that retrieves data from the right place  
  const getFieldSmart = (key: string, defaultValue: any = ''): any => {
    if (shouldStoreInSection(key)) {
      const sectionName = getSectionName(currentType);
      if (sectionName) {
        return getSectionValue(sectionName, typeIndex, key, defaultValue);
      }
    }
    return getAgnosticValue(key, defaultValue);
  };

  // Local state management for decimal inputs
  const [localInputs, setLocalInputs] = useState<Record<string, string>>({});

  // Create a wrapper for number input handlers
  const createNumberInputHandler = useCallback((
    updateFn: (value: string) => void,
    inputKey: string
  ) => {
    return (valueString: string) => {
      // Update local state to preserve partial decimal input like "12."
      setLocalInputs(prev => ({ ...prev, [inputKey]: valueString }));
      // Still call the original update function
      updateFn(valueString);
    };
  }, []);

  // Get the display value for a number input (local state or parsed value)
  const getNumberInputValue = useCallback((
    getValue: () => any,
    inputKey: string
  ) => {
    // If we have local state for this input, use it (preserves "12." while typing)
    if (inputKey in localInputs) {
      return localInputs[inputKey];
    }
    // Otherwise parse the value from Redux
    const value = getValue();
    return parseFloat(value || '0');
  }, [localInputs]);

  // Handle type change
  const handleTypeChange = (value: string) => {
    const updated = [...selectedTypes];
    updated[policyIndex] = value;
    const normalized = updated.filter((v) => v && v.trim().length > 0);
    dispatch(setTypeOfPolicy(normalized.join(',')));
  };

  // Get the array index for this specific policy type
  const getTypeIndex = () => {
    let count = -1;
    for (let i = 0; i <= policyIndex; i++) {
      if (selectedTypes[i] === currentType) {
        count++;
      }
    }
    return count;
  };

  const typeIndex = getTypeIndex();

  const renderLifeInsuranceFields = () => {
    const termPermanent = getSectionValue(
      'life_insurance',
      typeIndex,
      'term_permanent',
    );
    return (
      <>
        <HStack spacing={4} align="stretch">
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Term/Permanent</FormLabel>
            <Select
              variant="main"
              value={termPermanent}
              onChange={(e) =>
                updateSectionField(
                  'life_insurance',
                  typeIndex,
                  'term_permanent',
                  e.target.value,
                )
              }
              placeholder="Select"
              isDisabled={isLocked}
            >
              <option value="Term">Term</option>
              <option value="Permanent">Permanent</option>
            </Select>
          </FormControl>
        </HStack>

        {termPermanent === 'Term' && (
          <FormControl isRequired>
            <FormLabel ml="0">Term Length</FormLabel>
            <Select
              variant="main"
              value={getSectionValue(
                'life_insurance',
                typeIndex,
                'term_length',
              )}
              onChange={(e) =>
                updateSectionField(
                  'life_insurance',
                  typeIndex,
                  'term_length',
                  e.target.value,
                )
              }
              placeholder="Select term length"
              isDisabled={isLocked}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
              <option value="30">30</option>
              <option value="40">40</option>
              <option value="100">100</option>
              <option value="other">Other</option>
            </Select>
          </FormControl>
        )}

        {termPermanent === 'Permanent' && (
          <>
            <FormControl isRequired>
              <FormLabel ml="0">Type</FormLabel>
              <Select
                variant="main"
                value={getSectionValue(
                  'life_insurance',
                  typeIndex,
                  'permanent_type',
                )}
                onChange={(e) =>
                  updateSectionField(
                    'life_insurance',
                    typeIndex,
                    'permanent_type',
                    e.target.value,
                  )
                }
                placeholder="Select type"
                isDisabled={isLocked}
              >
                <option value="PAR">PAR</option>
                <option value="Non-PAR">Non-PAR</option>
                <option value="UL">UL</option>
                <option value="guaranteed issue">Guaranteed Issue</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel ml="0">Payment Length</FormLabel>
              <Select
                variant="main"
                value={getSectionValue(
                  'life_insurance',
                  typeIndex,
                  'payment_length',
                )}
                onChange={(e) =>
                  updateSectionField(
                    'life_insurance',
                    typeIndex,
                    'payment_length',
                    e.target.value,
                  )
                }
                placeholder="Select payment length"
                isDisabled={isLocked}
              >
                <option value="10pay">10pay</option>
                <option value="20pay">20pay</option>
                <option value="life pay">Life pay</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel ml="0">Additional Deposit</FormLabel>
              <Checkbox
                isChecked={getSectionValue(
                  'life_insurance',
                  typeIndex,
                  'additional_deposit',
                  false,
                )}
                onChange={(e) =>
                  updateSectionField(
                    'life_insurance',
                    typeIndex,
                    'additional_deposit',
                    e.target.checked,
                  )
                }
                isDisabled={isLocked}
              >
                Yes
              </Checkbox>
            </FormControl>
          </>
        )}

        <HStack spacing={4} align="stretch">
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Benefit Amount</FormLabel>
            <NumberInput
              value={getNumberInputValue(() => getFieldSmart('benefit_amount', '0'), 'benefit_amount')}
              onChange={createNumberInputHandler(
                (valueString) => updateFieldSmart('benefit_amount', valueString),
                'benefit_amount'
              )}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked}
            >
              <NumberInputField placeholder="0.00" />
            </NumberInput>
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Premium Amount</FormLabel>
            <NumberInput
              value={getNumberInputValue(() => getFieldSmart('premium_amount', '0'), 'premium_amount')}
              onChange={createNumberInputHandler(
                (valueString) => updateFieldSmart('premium_amount', valueString),
                'premium_amount'
              )}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked && !canEditPremium}
            >
              <NumberInputField placeholder="0.00" />
            </NumberInput>
            <PremiumWarningAlert
              premiumAmount={parseFloat(getFieldSmart('premium_amount', '0'))}
              premiumConfirmed={premiumConfirmed}
              onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
              isLocked={isLocked}
            />
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Monthly/Annual</FormLabel>
            <Select
              variant="main"
              value={getAgnosticValue('monthly_annual')}
              onChange={(e) =>
                updateAgnosticField('monthly_annual', e.target.value)
              }
              placeholder="Select"
              isDisabled={isLocked}
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </Select>
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Policy Number</FormLabel>
            <Input
              variant="main"
              value={getAgnosticValue('policy_number')}
              onChange={(e) =>
                updateAgnosticField('policy_number', e.target.value)
              }
              placeholder="Enter policy number"
              isDisabled={isLocked}
            />
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormLabel ml="0">Carrier</FormLabel>
          <Select
            variant="main"
            value={getSectionValue('life_insurance', typeIndex, 'carrier', '')}
            onChange={(e) =>
              updateSectionField(
                'life_insurance',
                typeIndex,
                'carrier',
                e.target.value,
              )
            }
            placeholder="Select carrier"
            isDisabled={isLocked}
          >
            {getCarriersForPolicyType('Life insurance', []).map((carrier: string) => (
              <option key={carrier} value={carrier}>
                {carrier}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel ml="0">Is there a rider?</FormLabel>
          <RadioGroup
            value={getSectionValue('life_insurance', typeIndex, 'has_rider', false) ? 'yes' : 'no'}
            onChange={(value) =>
              updateSectionField(
                'life_insurance',
                typeIndex,
                'has_rider',
                value === 'yes',
              )
            }
            isDisabled={isLocked}
          >
            <HStack spacing={8}>
              <Radio value="yes" isDisabled={isLocked}>
                Yes
              </Radio>
              <Radio value="no" isDisabled={isLocked}>
                No
              </Radio>
            </HStack>
          </RadioGroup>
        </FormControl>

        {getSectionValue('life_insurance', typeIndex, 'has_rider', false) && (
          <>
            <FormControl isRequired>
              <FormLabel ml="0">Select Riders</FormLabel>
              <Box>
                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    width="100%"
                    textAlign="left"
                    fontWeight="normal"
                    variant="outline"
                    isDisabled={isLocked}
                    bg="white"
                    _hover={{ bg: 'gray.50' }}
                  >
                    {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).length > 0
                      ? `${(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).length} rider(s) selected`
                      : 'Select riders...'}
                  </MenuButton>
                  <MenuList minWidth="240px">
                    <MenuItem
                      onClick={() => {
                        const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                        const newRiders = currentRiders.includes('term_life')
                          ? currentRiders.filter((r: string) => r !== 'term_life')
                          : [...currentRiders, 'term_life'];
                        updateSectionField('life_insurance', typeIndex, 'selected_riders', newRiders);
                      }}
                    >
                      <Checkbox
                        isChecked={(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('term_life')}
                        pointerEvents="none"
                        mr={2}
                      />
                      Term Life
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                        const newRiders = currentRiders.includes('critical_illness')
                          ? currentRiders.filter((r: string) => r !== 'critical_illness')
                          : [...currentRiders, 'critical_illness'];
                        updateSectionField('life_insurance', typeIndex, 'selected_riders', newRiders);
                      }}
                    >
                      <Checkbox
                        isChecked={(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('critical_illness')}
                        pointerEvents="none"
                        mr={2}
                      />
                      Critical Illness
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                        const newRiders = currentRiders.includes('disability')
                          ? currentRiders.filter((r: string) => r !== 'disability')
                          : [...currentRiders, 'disability'];
                        updateSectionField('life_insurance', typeIndex, 'selected_riders', newRiders);
                      }}
                    >
                      <Checkbox
                        isChecked={(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('disability')}
                        pointerEvents="none"
                        mr={2}
                      />
                      Disability
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                        const newRiders = currentRiders.includes('other')
                          ? currentRiders.filter((r: string) => r !== 'other')
                          : [...currentRiders, 'other'];
                        updateSectionField('life_insurance', typeIndex, 'selected_riders', newRiders);
                      }}
                    >
                      <Checkbox
                        isChecked={(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('other')}
                        pointerEvents="none"
                        mr={2}
                      />
                      Other
                    </MenuItem>
                  </MenuList>
                </Menu>

                {/* Display selected riders as tags */}
                {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).length > 0 && (
                  <Wrap spacing={2} mt={2}>
                    {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('term_life') && (
                      <WrapItem>
                        <Tag size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>Term Life</TagLabel>
                          {!isLocked && (
                            <TagCloseButton
                              onClick={() => {
                                const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                                updateSectionField('life_insurance', typeIndex, 'selected_riders', currentRiders.filter((r: string) => r !== 'term_life'));
                              }}
                            />
                          )}
                        </Tag>
                      </WrapItem>
                    )}
                    {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('critical_illness') && (
                      <WrapItem>
                        <Tag size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>Critical Illness</TagLabel>
                          {!isLocked && (
                            <TagCloseButton
                              onClick={() => {
                                const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                                updateSectionField('life_insurance', typeIndex, 'selected_riders', currentRiders.filter((r: string) => r !== 'critical_illness'));
                              }}
                            />
                          )}
                        </Tag>
                      </WrapItem>
                    )}
                    {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('disability') && (
                      <WrapItem>
                        <Tag size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>Disability</TagLabel>
                          {!isLocked && (
                            <TagCloseButton
                              onClick={() => {
                                const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                                updateSectionField('life_insurance', typeIndex, 'selected_riders', currentRiders.filter((r: string) => r !== 'disability'));
                              }}
                            />
                          )}
                        </Tag>
                      </WrapItem>
                    )}
                    {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('other') && (
                      <WrapItem>
                        <Tag size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>Other</TagLabel>
                          {!isLocked && (
                            <TagCloseButton
                              onClick={() => {
                                const currentRiders = getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || [];
                                updateSectionField('life_insurance', typeIndex, 'selected_riders', currentRiders.filter((r: string) => r !== 'other'));
                              }}
                            />
                          )}
                        </Tag>
                      </WrapItem>
                    )}
                  </Wrap>
                )}
              </Box>
            </FormControl>

            {/* Term Life Rider */}
            {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('term_life') && (
              <Box borderWidth="1px" borderRadius="md" p={4} mt={4}>
                <Text fontWeight="bold" fontSize="md" mb={3}>
                  Term Life Rider
                </Text>
                <FormControl isRequired>
                  <FormLabel ml="0">Coverage Amount</FormLabel>
                  <NumberInput
                    value={getNumberInputValue(() => getSectionValue('life_insurance', typeIndex, 'rider_term_life_amount', '0'), `rider_term_life_amount_${typeIndex}`)}
                    onChange={createNumberInputHandler(
                      (valueString) => updateSectionField('life_insurance', typeIndex, 'rider_term_life_amount', valueString),
                      `rider_term_life_amount_${typeIndex}`
                    )}
                    min={0}
                    step={0.01}
                    precision={2}
                    isDisabled={isLocked}
                  >
                    <NumberInputField placeholder="0.00" />
                  </NumberInput>
                </FormControl>
              </Box>
            )}

            {/* Critical Illness Rider - Same form as Critical Illness product */}
            {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('critical_illness') && (
              <Box borderWidth="1px" borderRadius="md" p={4} mt={4}>
                <Text fontWeight="bold" fontSize="md" mb={3}>
                  Critical Illness Rider
                </Text>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel ml="0">Type</FormLabel>
                    <Select
                      variant="main"
                      value={getSectionValue('life_insurance', typeIndex, 'rider_ci_type', '')}
                      onChange={(e) =>
                        updateSectionField('life_insurance', typeIndex, 'rider_ci_type', e.target.value)
                      }
                      placeholder="Select type"
                      isDisabled={isLocked}
                    >
                      <option value="Term">Term</option>
                      <option value="Level Pay to 75">Level Pay to 75</option>
                      <option value="Life pay to age 100">Life pay to age 100</option>
                    </Select>
                  </FormControl>

                  <HStack spacing={4} align="stretch">
                    <FormControl flex={1} isRequired>
                      <FormLabel ml="0">Premium Amount</FormLabel>
                      <NumberInput
                        value={getNumberInputValue(() => getSectionValue('life_insurance', typeIndex, 'rider_ci_premium_amount', '0'), `rider_ci_premium_amount_${typeIndex}`)}
                        onChange={createNumberInputHandler(
                          (valueString) => updateSectionField('life_insurance', typeIndex, 'rider_ci_premium_amount', valueString),
                          `rider_ci_premium_amount_${typeIndex}`
                        )}
                        min={0}
                        step={0.01}
                        precision={2}
                        isDisabled={isLocked && !canEditPremium}
                      >
                    <NumberInputField placeholder="0.00" />
                      </NumberInput>
                      <PremiumWarningAlert
                        premiumAmount={parseFloat(getSectionValue('life_insurance', typeIndex, 'rider_ci_premium_amount', '0'))}
                        premiumConfirmed={premiumConfirmed}
                        onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
                        isLocked={isLocked}
                      />
                    </FormControl>

                    <FormControl flex={1} isRequired>
                      <FormLabel ml="0">Premium Frequency</FormLabel>
                      <Select
                        variant="main"
                        value={getSectionValue('life_insurance', typeIndex, 'rider_ci_premium_frequency', '')}
                        onChange={(e) =>
                          updateSectionField('life_insurance', typeIndex, 'rider_ci_premium_frequency', e.target.value)
                        }
                        placeholder="Select"
                        isDisabled={isLocked}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annually">Annually</option>
                      </Select>
                    </FormControl>
                  </HStack>

                  <HStack spacing={4} align="stretch">
                    <FormControl flex={2} isRequired>
                      <FormLabel ml="0">Benefit Amount</FormLabel>
                      <NumberInput
                        value={getNumberInputValue(() => getSectionValue('life_insurance', typeIndex, 'rider_ci_benefit_amount', '0'), `rider_ci_benefit_amount_${typeIndex}`)}
                        onChange={createNumberInputHandler(
                          (valueString) => updateSectionField('life_insurance', typeIndex, 'rider_ci_benefit_amount', valueString),
                          `rider_ci_benefit_amount_${typeIndex}`
                        )}
                        min={0}
                        step={0.01}
                        precision={2}
                        isDisabled={isLocked}
                      >
                    <NumberInputField placeholder="0.00" />
                      </NumberInput>
                    </FormControl>

                    <FormControl flex={1}>
                      <FormLabel ml="0">Features (Optional)</FormLabel>
                      <HStack spacing={4}>
                        <Checkbox
                          isChecked={(getSectionValue('life_insurance', typeIndex, 'rider_ci_features', []) || []).includes('ROPD')}
                          onChange={(e) => {
                            const currentFeatures = getSectionValue('life_insurance', typeIndex, 'rider_ci_features', []) || [];
                            const newFeatures = e.target.checked
                              ? [...currentFeatures, 'ROPD']
                              : currentFeatures.filter((f: string) => f !== 'ROPD');
                            updateSectionField('life_insurance', typeIndex, 'rider_ci_features', newFeatures);
                          }}
                          isDisabled={isLocked}
                        >
                          ROPD
                        </Checkbox>
                        <Checkbox
                          isChecked={(getSectionValue('life_insurance', typeIndex, 'rider_ci_features', []) || []).includes('ROPX')}
                          onChange={(e) => {
                            const currentFeatures = getSectionValue('life_insurance', typeIndex, 'rider_ci_features', []) || [];
                            const newFeatures = e.target.checked
                              ? [...currentFeatures, 'ROPX']
                              : currentFeatures.filter((f: string) => f !== 'ROPX');
                            updateSectionField('life_insurance', typeIndex, 'rider_ci_features', newFeatures);
                          }}
                          isDisabled={isLocked}
                        >
                          ROPX
                        </Checkbox>
                      </HStack>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>
            )}

            {/* Disability Rider - Same form as Disability product */}
            {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('disability') && (
              <Box borderWidth="1px" borderRadius="md" p={4} mt={4}>
                <Text fontWeight="bold" fontSize="md" mb={3}>
                  Disability Rider
                </Text>
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel ml="0">Benefit Amount</FormLabel>
                    <NumberInput
                      value={getNumberInputValue(() => getSectionValue('life_insurance', typeIndex, 'rider_disability_benefit_amount', '0'), `rider_disability_benefit_amount_${typeIndex}`)}
                      onChange={createNumberInputHandler(
                        (valueString) => updateSectionField('life_insurance', typeIndex, 'rider_disability_benefit_amount', valueString),
                        `rider_disability_benefit_amount_${typeIndex}`
                      )}
                      min={0}
                      step={0.01}
                      precision={2}
                      isDisabled={isLocked}
                    >
                    <NumberInputField placeholder="0.00" />
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel ml="0">Integrated with LTD</FormLabel>
                    <Checkbox
                      isChecked={getSectionValue('life_insurance', typeIndex, 'rider_disability_integrated_with_ltd', false)}
                      onChange={(e) =>
                        updateSectionField('life_insurance', typeIndex, 'rider_disability_integrated_with_ltd', e.target.checked)
                      }
                      isDisabled={isLocked}
                    >
                      Yes
                    </Checkbox>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel ml="0">Taxable</FormLabel>
                    <RadioGroup
                      value={getSectionValue('life_insurance', typeIndex, 'rider_disability_taxable', false) ? 'yes' : 'no'}
                      onChange={(value) =>
                        updateSectionField('life_insurance', typeIndex, 'rider_disability_taxable', value === 'yes')
                      }
                      isDisabled={isLocked}
                    >
                      <HStack spacing={8}>
                        <Radio value="yes" isDisabled={isLocked}>
                          Yes
                        </Radio>
                        <Radio value="no" isDisabled={isLocked}>
                          No
                        </Radio>
                      </HStack>
                    </RadioGroup>
                  </FormControl>

                  <HStack spacing={4} align="stretch">
                    <FormControl flex={1} isRequired>
                      <FormLabel ml="0">Premium Amount</FormLabel>
                      <NumberInput
                        value={getNumberInputValue(() => getSectionValue('life_insurance', typeIndex, 'rider_disability_premium_amount', '0'), `rider_disability_premium_amount_${typeIndex}`)}
                        onChange={createNumberInputHandler(
                          (valueString) => updateSectionField('life_insurance', typeIndex, 'rider_disability_premium_amount', valueString),
                          `rider_disability_premium_amount_${typeIndex}`
                        )}
                        min={0}
                        step={0.01}
                        precision={2}
                        isDisabled={isLocked && !canEditPremium}
                      >
                    <NumberInputField placeholder="0.00" />
                      </NumberInput>
                      <PremiumWarningAlert
                        premiumAmount={parseFloat(getSectionValue('life_insurance', typeIndex, 'rider_disability_premium_amount', '0'))}
                        premiumConfirmed={premiumConfirmed}
                        onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
                        isLocked={isLocked}
                      />
                    </FormControl>

                    <FormControl flex={1} isRequired>
                      <FormLabel ml="0">Premium Frequency</FormLabel>
                      <Select
                        variant="main"
                        value={getSectionValue('life_insurance', typeIndex, 'rider_disability_premium_frequency', '')}
                        onChange={(e) =>
                          updateSectionField('life_insurance', typeIndex, 'rider_disability_premium_frequency', e.target.value)
                        }
                        placeholder="Select"
                        isDisabled={isLocked}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="annually">Annually</option>
                      </Select>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>
            )}

            {/* Other Rider */}
            {(getSectionValue('life_insurance', typeIndex, 'selected_riders', []) || []).includes('other') && (
              <Box borderWidth="1px" borderRadius="md" p={4} mt={4}>
                <Text fontWeight="bold" fontSize="md" mb={3}>
                  Other Rider
                </Text>
                <FormControl isRequired>
                  <FormLabel ml="0">Note</FormLabel>
                  <Textarea
                    variant="main"
                    value={getSectionValue('life_insurance', typeIndex, 'rider_other_note', '') || ''}
                    onChange={(e) =>
                      updateSectionField('life_insurance', typeIndex, 'rider_other_note', e.target.value)
                    }
                    placeholder="Enter details about the other rider..."
                    isDisabled={isLocked}
                    rows={4}
                    borderColor={!getSectionValue('life_insurance', typeIndex, 'rider_other_note', '') ? 'red' : 'inherit'}
                  />
                </FormControl>
              </Box>
            )}

            <Text fontSize="sm" color="gray.500" fontStyle="italic" mt={2}>
              Note: Riders use the same carrier as the main policy.
            </Text>
          </>
        )}
      </>
    );
  };

  const renderCriticalIllnessFields = () => {
    const features =
      getSectionValue('critical_illness', typeIndex, 'features', []) || [];
    return (
      <>
        <FormControl isRequired>
          <FormLabel ml="0">Type</FormLabel>
          <Select
            variant="main"
            value={getSectionValue('critical_illness', typeIndex, 'type')}
            onChange={(e) =>
              updateSectionField(
                'critical_illness',
                typeIndex,
                'type',
                e.target.value,
              )
            }
            placeholder="Select type"
            isDisabled={isLocked}
          >
            <option value="Term">Term</option>
            <option value="Level Pay to 75">Level Pay to 75</option>
            <option value="Life pay to age 100">Life pay to age 100</option>
          </Select>
        </FormControl>

        <HStack spacing={4} align="stretch">
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Premium Amount</FormLabel>
            <NumberInput
              value={getNumberInputValue(() => getSectionValue('critical_illness', typeIndex, 'premium_amount', '0'), `ci_premium_amount_${typeIndex}`)}
              onChange={createNumberInputHandler(
                (valueString) => updateSectionField('critical_illness', typeIndex, 'premium_amount', valueString),
                `ci_premium_amount_${typeIndex}`
              )}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked && !canEditPremium}
            >
              <NumberInputField placeholder="0.00" />
            </NumberInput>
            <PremiumWarningAlert
              premiumAmount={parseFloat(getSectionValue('critical_illness', typeIndex, 'premium_amount', '0'))}
              premiumConfirmed={premiumConfirmed}
              onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
              isLocked={isLocked}
            />
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Premium Frequency</FormLabel>
            <Select
              variant="main"
              value={getSectionValue(
                'critical_illness',
                typeIndex,
                'premium_frequency',
              )}
              onChange={(e) =>
                updateSectionField(
                  'critical_illness',
                  typeIndex,
                  'premium_frequency',
                  e.target.value,
                )
              }
              placeholder="Select"
              isDisabled={isLocked}
            >
              <option value="monthly">Monthly</option>
              <option value="annually">Annually</option>
            </Select>
          </FormControl>
        </HStack>

        <HStack spacing={4} align="stretch">
          <FormControl flex={2} isRequired>
            <FormLabel ml="0">Benefit Amount</FormLabel>
            <NumberInput
              value={getNumberInputValue(() => getSectionValue('critical_illness', typeIndex, 'benefit_amount', '0'), `ci_benefit_amount_${typeIndex}`)}
              onChange={createNumberInputHandler(
                (valueString) => updateSectionField('critical_illness', typeIndex, 'benefit_amount', valueString),
                `ci_benefit_amount_${typeIndex}`
              )}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked}
            >
              <NumberInputField placeholder="0.00" />
            </NumberInput>
          </FormControl>

          <FormControl flex={1}>
            <FormLabel ml="0">Features (Optional)</FormLabel>
            <HStack spacing={4}>
              <Checkbox
                isChecked={features.includes('ROPD')}
                onChange={(e) => {
                  const newFeatures = e.target.checked
                    ? [...features, 'ROPD']
                    : features.filter((f: string) => f !== 'ROPD');
                  updateSectionField(
                    'critical_illness',
                    typeIndex,
                    'features',
                    newFeatures,
                  );
                }}
                isDisabled={isLocked}
              >
                ROPD
              </Checkbox>
              <Checkbox
                isChecked={features.includes('ROPX')}
                onChange={(e) => {
                  const newFeatures = e.target.checked
                    ? [...features, 'ROPX']
                    : features.filter((f: string) => f !== 'ROPX');
                  updateSectionField(
                    'critical_illness',
                    typeIndex,
                    'features',
                    newFeatures,
                  );
                }}
                isDisabled={isLocked}
              >
                ROPX
              </Checkbox>
            </HStack>
          </FormControl>
        </HStack>

        <FormControl isRequired>
          <FormLabel ml="0">Carrier</FormLabel>
          <Select
            variant="main"
            value={getSectionValue('critical_illness', typeIndex, 'carrier', '')}
            onChange={(e) =>
              updateSectionField(
                'critical_illness',
                typeIndex,
                'carrier',
                e.target.value,
              )
            }
            placeholder="Select carrier"
            isDisabled={isLocked}
          >
            {getCarriersForPolicyType('Critical Illness', []).map((carrier: string) => (
              <option key={carrier} value={carrier}>
                {carrier}
              </option>
            ))}
          </Select>
        </FormControl>
      </>
    );
  };

  const renderDisabilityFields = () => (
    <>
      <FormControl isRequired>
        <FormLabel ml="0">Benefit Amount</FormLabel>
        <NumberInput
          value={getNumberInputValue(() => getSectionValue('disability', typeIndex, 'benefit_amount', '0'), `disability_benefit_amount_${typeIndex}`)}
          onChange={createNumberInputHandler(
            (valueString) => updateSectionField('disability', typeIndex, 'benefit_amount', valueString),
            `disability_benefit_amount_${typeIndex}`
          )}
          min={0}
          step={0.01}
          precision={2}
          isDisabled={isLocked}
        >
                    <NumberInputField placeholder="0.00" />
        </NumberInput>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Integrated with LTD</FormLabel>
        <Checkbox
          isChecked={getSectionValue(
            'disability',
            typeIndex,
            'integrated_with_ltd',
            false,
          )}
          onChange={(e) =>
            updateSectionField(
              'disability',
              typeIndex,
              'integrated_with_ltd',
              e.target.checked,
            )
          }
          isDisabled={isLocked}
        >
          Yes
        </Checkbox>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Taxable</FormLabel>
        <RadioGroup
          value={
            getSectionValue('disability', typeIndex, 'taxable') ? 'yes' : 'no'
          }
          onChange={(value) =>
            updateSectionField(
              'disability',
              typeIndex,
              'taxable',
              value === 'yes',
            )
          }
          isDisabled={isLocked}
        >
          <HStack spacing={8}>
            <Radio value="yes" isDisabled={isLocked}>
              Yes
            </Radio>
            <Radio value="no" isDisabled={isLocked}>
              No
            </Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      <HStack spacing={4} align="stretch">
        <FormControl flex={1} isRequired>
          <FormLabel ml="0">Premium Amount</FormLabel>
          <NumberInput
            value={getNumberInputValue(() => getSectionValue('disability', typeIndex, 'premium_amount', '0'), `disability_premium_amount_${typeIndex}`)}
            onChange={createNumberInputHandler(
              (valueString) => updateSectionField('disability', typeIndex, 'premium_amount', valueString),
              `disability_premium_amount_${typeIndex}`
            )}
            min={0}
            step={0.01}
            precision={2}
            isDisabled={isLocked && !canEditPremium}
          >
                    <NumberInputField placeholder="0.00" />
          </NumberInput>
          <PremiumWarningAlert
            premiumAmount={parseFloat(getSectionValue('disability', typeIndex, 'premium_amount', '0'))}
            premiumConfirmed={premiumConfirmed}
            onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
            isLocked={isLocked}
          />
        </FormControl>

        <FormControl flex={1} isRequired>
          <FormLabel ml="0">Premium Frequency</FormLabel>
          <Select
            variant="main"
            value={getSectionValue(
              'disability',
              typeIndex,
              'premium_frequency',
            )}
            onChange={(e) =>
              updateSectionField(
                'disability',
                typeIndex,
                'premium_frequency',
                e.target.value,
              )
            }
            placeholder="Select"
            isDisabled={isLocked}
          >
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
          </Select>
        </FormControl>
      </HStack>

      <FormControl isRequired>
        <FormLabel ml="0">Carrier</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('disability', typeIndex, 'carrier', '')}
          onChange={(e) =>
            updateSectionField(
              'disability',
              typeIndex,
              'carrier',
              e.target.value,
            )
          }
          placeholder="Select carrier"
          isDisabled={isLocked}
        >
          {getCarriersForPolicyType('Disability', []).map((carrier: string) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </Select>
      </FormControl>
    </>
  );

  const renderSegregatedFundsFields = () => (
    <>
      <FormControl isRequired>
        <FormLabel ml="0">Type</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('segregated_funds', typeIndex, 'type')}
          onChange={(e) =>
            updateSectionField(
              'segregated_funds',
              typeIndex,
              'type',
              e.target.value,
            )
          }
          placeholder="Select type"
          isDisabled={isLocked}
        >
          <option value="TFSA">TFSA</option>
          <option value="RRSP">RRSP</option>
          <option value="Non-registered">Non-registered</option>
          <option value="RESP">RESP</option>
          <option value="LIF">LIF</option>
          <option value="RIF">RIF</option>
          <option value="LIRA">LIRA</option>
          <option value="other">Other</option>
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Carrier</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('segregated_funds', typeIndex, 'carrier', '')}
          onChange={(e) =>
            updateSectionField(
              'segregated_funds',
              typeIndex,
              'carrier',
              e.target.value,
            )
          }
          placeholder="Select carrier"
          isDisabled={isLocked}
        >
          {getCarriersForPolicyType('Segregated Funds', []).map((carrier: string) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Initial Investment Amount</FormLabel>
        <NumberInput
          value={getNumberInputValue(() => getSectionValue('segregated_funds', typeIndex, 'initial_investment_amount', '0'), `seg_funds_initial_${typeIndex}`)}
          onChange={createNumberInputHandler(
            (valueString) => updateSectionField('segregated_funds', typeIndex, 'initial_investment_amount', valueString),
            `seg_funds_initial_${typeIndex}`
          )}
          min={0}
          step={0.01}
          precision={2}
          isDisabled={isLocked}
        >
                    <NumberInputField placeholder="0.00" />
        </NumberInput>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Regular Contribution Amount</FormLabel>
        <Select
          variant="main"
          value={getSectionValue(
            'segregated_funds',
            typeIndex,
            'regular_contribution_amount',
          )}
          onChange={(e) =>
            updateSectionField(
              'segregated_funds',
              typeIndex,
              'regular_contribution_amount',
              e.target.value,
            )
          }
          placeholder="Select"
          isDisabled={isLocked}
        >
          <option value="monthly">Monthly</option>
          <option value="annual">Annual</option>
          <option value="n/a">N/A</option>
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Load Charge</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('segregated_funds', typeIndex, 'load_charge')}
          onChange={(e) =>
            updateSectionField(
              'segregated_funds',
              typeIndex,
              'load_charge',
              e.target.value,
            )
          }
          placeholder="Select"
          isDisabled={isLocked}
        >
          <option value="FEL 0%">FEL 0%</option>
          <option value="CB3">CB3</option>
          <option value="CB5">CB5</option>
          <option value="other">Other</option>
        </Select>
      </FormControl>
    </>
  );

  const renderGroupBenefitsFields = () => (
    <>
      <FormControl isRequired>
        <FormLabel ml="0">Size of Group</FormLabel>
        <Input
          variant="main"
          type="text"
          value={getSectionValue('group_benefits', typeIndex, 'size_of_group')}
          onChange={(e) =>
            updateSectionField(
              'group_benefits',
              typeIndex,
              'size_of_group',
              e.target.value,
            )
          }
          placeholder="Enter number of employees (1-100+)"
          isDisabled={isLocked}
        />
      </FormControl>

      <FormControl isRequired>
        <DateField
          label="Date in Force"
          date={getDateFromString(
            getSectionValue('group_benefits', typeIndex, 'date_in_force') || '',
          )}
          onChange={(date: Date | null) =>
            updateSectionField(
              'group_benefits',
              typeIndex,
              'date_in_force',
              date ? getDateString(date) : null,
            )
          }
          required
          isDisabled={isLocked}
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Client has had group benefits before</FormLabel>
        <RadioGroup
          value={
            getSectionValue(
              'group_benefits',
              typeIndex,
              'client_has_had_group_benefits_before',
            )
              ? 'yes'
              : 'no'
          }
          onChange={(value) =>
            updateSectionField(
              'group_benefits',
              typeIndex,
              'client_has_had_group_benefits_before',
              value === 'yes',
            )
          }
          isDisabled={isLocked}
        >
          <HStack spacing={8}>
            <Radio value="yes" isDisabled={isLocked}>
              Yes
            </Radio>
            <Radio value="no" isDisabled={isLocked}>
              No
            </Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Premium Amount</FormLabel>
        <NumberInput
          value={getNumberInputValue(() => getSectionValue('group_benefits', typeIndex, 'premium_amount', '0'), `group_benefits_premium_${typeIndex}`)}
          onChange={createNumberInputHandler(
            (valueString) => updateSectionField('group_benefits', typeIndex, 'premium_amount', valueString),
            `group_benefits_premium_${typeIndex}`
          )}
          min={0}
          step={0.01}
          precision={2}
          isDisabled={isLocked && !canEditPremium}
        >
                    <NumberInputField placeholder="0.00" />
        </NumberInput>
        <PremiumWarningAlert
          premiumAmount={parseFloat(getSectionValue('group_benefits', typeIndex, 'premium_amount', '0'))}
          premiumConfirmed={premiumConfirmed}
          onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
          isLocked={isLocked}
        />
      </FormControl>

      <HStack spacing={4}>
        <FormControl>
          <FormLabel ml="0">HSA</FormLabel>
          <Checkbox
            isChecked={getSectionValue(
              'group_benefits',
              typeIndex,
              'hsa',
              false,
            )}
            onChange={(e) =>
              updateSectionField(
                'group_benefits',
                typeIndex,
                'hsa',
                e.target.checked,
              )
            }
            isDisabled={isLocked}
          >
            Yes
          </Checkbox>
        </FormControl>

        <FormControl isRequired>
          <FormLabel ml="0">Tiered Plan</FormLabel>
          <Checkbox
            isChecked={getSectionValue(
              'group_benefits',
              typeIndex,
              'tiered_plan',
              false,
            )}
            onChange={(e) =>
              updateSectionField(
                'group_benefits',
                typeIndex,
                'tiered_plan',
                e.target.checked,
              )
            }
            isDisabled={isLocked}
          >
            Yes
          </Checkbox>
        </FormControl>

        <FormControl isRequired>
          <FormLabel ml="0">Group Retirement Services Included</FormLabel>
          <Checkbox
            isChecked={getSectionValue(
              'group_benefits',
              typeIndex,
              'group_retirement_services_included',
              false,
            )}
            onChange={(e) =>
              updateSectionField(
                'group_benefits',
                typeIndex,
                'group_retirement_services_included',
                e.target.checked,
              )
            }
            isDisabled={isLocked}
          >
            Yes
          </Checkbox>
        </FormControl>
      </HStack>

      <FormControl isRequired>
        <FormLabel ml="0">Carrier</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('group_benefits', typeIndex, 'carrier', '')}
          onChange={(e) =>
            updateSectionField(
              'group_benefits',
              typeIndex,
              'carrier',
              e.target.value,
            )
          }
          placeholder="Select carrier"
          isDisabled={isLocked}
        >
          {getCarriersForPolicyType('Group Benefits', []).map((carrier: string) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </Select>
      </FormControl>
    </>
  );

  const renderGroupRetirementServicesFields = () => (
    <>
      <FormControl isRequired>
        <FormLabel ml="0">Type</FormLabel>
        <Select
          variant="main"
          value={getSectionValue(
            'group_retirement_services',
            typeIndex,
            'type',
          )}
          onChange={(e) =>
            updateSectionField(
              'group_retirement_services',
              typeIndex,
              'type',
              e.target.value,
            )
          }
          placeholder="Select type"
          isDisabled={isLocked}
        >
          <option value="RRSP">RRSP</option>
          <option value="RRSP/TFSA">RRSP/TFSA</option>
          <option value="RRSP/TFSA/DPSP">RRSP/TFSA/DPSP</option>
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Company Match Percentage</FormLabel>
        <Select
          variant="main"
          value={getSectionValue(
            'group_retirement_services',
            typeIndex,
            'company_match_percentage',
          )}
          onChange={(e) =>
            updateSectionField(
              'group_retirement_services',
              typeIndex,
              'company_match_percentage',
              e.target.value,
            )
          }
          placeholder="Select"
          isDisabled={isLocked}
        >
          <option value="2">2</option>
          <option value="2.5">2.5</option>
          <option value="3">3</option>
          <option value="3.5">3.5</option>
          <option value="4">4</option>
          <option value="4.5">4.5</option>
          <option value="5">5</option>
          <option value="5.5">5.5</option>
          <option value="6">6</option>
          <option value="6.5">6.5</option>
          <option value="7">7</option>
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Carrier</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('group_retirement_services', typeIndex, 'carrier', '')}
          onChange={(e) =>
            updateSectionField(
              'group_retirement_services',
              typeIndex,
              'carrier',
              e.target.value,
            )
          }
          placeholder="Select carrier"
          isDisabled={isLocked}
        >
          {getCarriersForPolicyType('Group Retirement Services', []).map((carrier: string) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Group Benefits</FormLabel>
        <RadioGroup
          value={
            getSectionValue(
              'group_retirement_services',
              typeIndex,
              'group_benefits',
            )
              ? 'yes'
              : 'no'
          }
          onChange={(value) =>
            updateSectionField(
              'group_retirement_services',
              typeIndex,
              'group_benefits',
              value === 'yes',
            )
          }
          isDisabled={isLocked}
        >
          <HStack spacing={8}>
            <Radio value="yes" isDisabled={isLocked}>
              Yes
            </Radio>
            <Radio value="no" isDisabled={isLocked}>
              No
            </Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      <FormControl isRequired>
        <FormLabel ml="0">Contribution Total Amount</FormLabel>
        <NumberInput
          value={getNumberInputValue(() => getSectionValue('group_retirement_services', typeIndex, 'contribution_total_amount', '0'), `group_retirement_contribution_${typeIndex}`)}
          onChange={createNumberInputHandler(
            (valueString) => updateSectionField('group_retirement_services', typeIndex, 'contribution_total_amount', valueString),
            `group_retirement_contribution_${typeIndex}`
          )}
          min={0}
          step={0.01}
          precision={2}
          isDisabled={isLocked}
        >
                    <NumberInputField placeholder="0.00" />
        </NumberInput>
      </FormControl>
    </>
  );

  const renderTravelFields = () => {
    const travelType = getSectionValue('travel', typeIndex, 'type');
    return (
      <>
        <FormControl isRequired>
          <FormLabel ml="0">Type</FormLabel>
          <Select
            variant="main"
            value={travelType}
            onChange={(e) =>
              updateSectionField('travel', typeIndex, 'type', e.target.value)
            }
            placeholder="Select type"
            isDisabled={isLocked}
          >
            <option value="Canadians travelling">Canadians travelling</option>
            <option value="Visitors to Canada">Visitors to Canada</option>
            <option value="International Student">International Student</option>
          </Select>
        </FormControl>

        {travelType === 'Visitors to Canada' && (
          <FormControl isRequired>
            <FormLabel ml="0">Supervisa</FormLabel>
            <RadioGroup
              value={
                getSectionValue('travel', typeIndex, 'supervisa') ? 'yes' : 'no'
              }
              onChange={(value) =>
                updateSectionField(
                  'travel',
                  typeIndex,
                  'supervisa',
                  value === 'yes',
                )
              }
              isDisabled={isLocked}
            >
              <HStack spacing={8}>
                <Radio value="yes" isDisabled={isLocked}>
                  Yes
                </Radio>
                <Radio value="no" isDisabled={isLocked}>
                  No
                </Radio>
              </HStack>
            </RadioGroup>
          </FormControl>
        )}

        <FormControl isRequired>
          <FormLabel ml="0">Single Trip or Multi Plan</FormLabel>
          <Select
            variant="main"
            value={getSectionValue(
              'travel',
              typeIndex,
              'single_trip_or_multi_plan',
            )}
            onChange={(e) =>
              updateSectionField(
                'travel',
                typeIndex,
                'single_trip_or_multi_plan',
                e.target.value,
              )
            }
            placeholder="Select"
            isDisabled={isLocked}
          >
            <option value="Single Trip">Single Trip</option>
            <option value="Multi Plan">Multi Plan</option>
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel ml="0">Carrier</FormLabel>
          <Select
            variant="main"
            value={getSectionValue('travel', typeIndex, 'carrier', '')}
            onChange={(e) =>
              updateSectionField(
                'travel',
                typeIndex,
                'carrier',
                e.target.value,
              )
            }
            placeholder="Select carrier"
            isDisabled={isLocked}
          >
            {getCarriersForPolicyType('Travel', []).map((carrier: string) => (
              <option key={carrier} value={carrier}>
                {carrier}
              </option>
            ))}
          </Select>
        </FormControl>

        <HStack spacing={4} align="stretch">
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Premium Amount</FormLabel>
            <NumberInput
              value={getNumberInputValue(() => getSectionValue('travel', typeIndex, 'premium_amount', '0'), `travel_premium_${typeIndex}`)}
              onChange={createNumberInputHandler(
                (valueString) => updateSectionField('travel', typeIndex, 'premium_amount', valueString),
                `travel_premium_${typeIndex}`
              )}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked && !canEditPremium}
            >
              <NumberInputField placeholder="0.00" />
            </NumberInput>
            <PremiumWarningAlert
              premiumAmount={parseFloat(getSectionValue('travel', typeIndex, 'premium_amount', '0'))}
              premiumConfirmed={premiumConfirmed}
              onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
              isLocked={isLocked}
            />
          </FormControl>

          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Coverage Amount</FormLabel>
            <NumberInput
              value={getNumberInputValue(() => getSectionValue('travel', typeIndex, 'coverage_amount', '0'), `travel_coverage_${typeIndex}`)}
              onChange={createNumberInputHandler(
                (valueString) => updateSectionField('travel', typeIndex, 'coverage_amount', valueString),
                `travel_coverage_${typeIndex}`
              )}
              min={0}
              step={0.01}
              precision={2}
              isDisabled={isLocked}
            >
              <NumberInputField placeholder="0.00" />
            </NumberInput>
          </FormControl>
        </HStack>
      </>
    );
  };

  const renderIndividualHealthDentalFields = () => (
    <>
      <HStack spacing={4} align="stretch">
        <FormControl flex={1} isRequired>
          <FormLabel ml="0">Premium Amount</FormLabel>
          <NumberInput
            value={getNumberInputValue(() => getSectionValue('individual_health_dental', typeIndex, 'premium_amount', '0'), `health_dental_premium_${typeIndex}`)}
            onChange={createNumberInputHandler(
              (valueString) => updateSectionField('individual_health_dental', typeIndex, 'premium_amount', valueString),
              `health_dental_premium_${typeIndex}`
            )}
            min={0}
            step={0.01}
            precision={2}
            isDisabled={isLocked && !canEditPremium}
          >
                    <NumberInputField placeholder="0.00" />
          </NumberInput>
          <PremiumWarningAlert
            premiumAmount={parseFloat(getSectionValue('individual_health_dental', typeIndex, 'premium_amount', '0'))}
            premiumConfirmed={premiumConfirmed}
            onConfirmChange={(checked) => dispatch(setPremiumConfirmed(checked))}
            isLocked={isLocked}
          />
        </FormControl>

        <FormControl flex={1} isRequired>
          <FormLabel ml="0">Coverage Amount</FormLabel>
          <NumberInput
            value={getNumberInputValue(() => getSectionValue('individual_health_dental', typeIndex, 'coverage_amount', '0'), `health_dental_coverage_${typeIndex}`)}
            onChange={createNumberInputHandler(
              (valueString) => updateSectionField('individual_health_dental', typeIndex, 'coverage_amount', valueString),
              `health_dental_coverage_${typeIndex}`
            )}
            min={0}
            step={0.01}
            precision={2}
            isDisabled={isLocked}
          >
                    <NumberInputField placeholder="0.00" />
          </NumberInput>
        </FormControl>
      </HStack>

      <FormControl isRequired>
        <FormLabel ml="0">Carrier</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('individual_health_dental', typeIndex, 'carrier', '')}
          onChange={(e) =>
            updateSectionField(
              'individual_health_dental',
              typeIndex,
              'carrier',
              e.target.value,
            )
          }
          placeholder="Select carrier"
          isDisabled={isLocked}
        >
          {getCarriersForPolicyType('Individual health and dental', []).map((carrier: string) => (
            <option key={carrier} value={carrier}>
              {carrier}
            </option>
          ))}
        </Select>
      </FormControl>
    </>
  );

  const renderOtherFields = () => (
    <>
      <FormControl>
        <FormLabel ml="0">Type</FormLabel>
        <Select
          variant="main"
          value={getSectionValue('other', typeIndex, 'type')}
          onChange={(e) =>
            updateSectionField('other', typeIndex, 'type', e.target.value)
          }
          placeholder="Select type"
          isDisabled={isLocked}
        >
          <option value="Mortgage referral">Mortgage referral</option>
          <option value="bank solution">Bank solution</option>
        </Select>
      </FormControl>
    </>
  );

  const renderPolicyDetails = () => {
    switch (currentType) {
      case 'Life insurance':
        return renderLifeInsuranceFields();
      case 'Critical Illness':
        return renderCriticalIllnessFields();
      case 'Disability':
        return renderDisabilityFields();
      case 'Segregated Funds':
        return renderSegregatedFundsFields();
      case 'Group Benefits':
        return renderGroupBenefitsFields();
      case 'Group Retirement Services':
        return renderGroupRetirementServicesFields();
      case 'Travel':
        return renderTravelFields();
      case 'Individual health and dental':
        return renderIndividualHealthDentalFields();
      case 'Other':
        return renderOtherFields();
      default:
        return null;
    }
  };

  return (
    <Card mb="20px">
      <VStack spacing={4} align="stretch">
        {/* Type of Policy Selection */}
        <HStack align="flex-start" spacing={2}>
          <FormControl flex={1} isRequired>
            <FormLabel ml="0">Type of Policy</FormLabel>
            <Select
              variant="main"
              value={currentType}
              onChange={(e) => handleTypeChange(e.target.value)}
              placeholder="Select type of policy"
              bg="white"
              isDisabled={isLocked}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px blue.500',
              }}
            >
              {POLICY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </FormControl>
          {!isLocked && showRemoveButton && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={onRemove}
              mt="32px"
            >
              Remove
            </Button>
          )}
        </HStack>

        {/* Policy Details */}
        {currentType && (
          <>
            <Text fontWeight="bold" fontSize="lg" mt={2}>
              Policy Details
            </Text>
            {renderPolicyDetails()}
          </>
        )}
      </VStack>
    </Card>
  );
};

export default LifePolicySection;
